import React, { useCallback, useMemo } from 'react';
import 'styles/ant-design.css';
import ExcelTable from 'components/ui/excel-table';
import FormSection from 'components/ui/form-section';
import PremiumStatusIndicator from 'components/ui/premium-status-indicator';
import { useTranslation } from 'react-i18next';
import { OriginalPremiumScheduleResponseDtoNocStatusEnum } from 'libs/pinnacle-sdk';
import {
  ECellValueType,
  EExcelTableColumnType,
  ExcelTableColDef,
  RowData,
  SelectionOption,
} from 'components/ui/excel-table/types';

import { useMutation, useQuery } from 'react-query';
import { premiumScheduleApi } from 'services/apis';
import { EQueryKeys } from 'constants/query-keys';
import { PremiumDueResponseDtoPremiumStatusEnum } from 'libs/pinnacle-sdk';
import dayjs from 'dayjs';
import { ICellRendererParams } from 'ag-grid-community';
import { PremiumScheduleValue } from './types';
import { handleError } from 'utils/form';

interface OriginalPremiumScheduleProps {
  currency: string;
  contractId?: string;
}

const OriginalPremiumSchedule: React.FC<OriginalPremiumScheduleProps> = ({
  contractId,
  currency,
}) => {
  const { t } = useTranslation();

  const {
    data: originalPremiumSchedule,
    isLoading: isLoadingOriginalPremiumSchedule,
    refetch,
  } = useQuery({
    queryKey: [EQueryKeys.GET_ORIGINAL_PREMIUM_SCHEDULE, contractId, currency],
    queryFn: () =>
      premiumScheduleApi.getOriginalPremiumSchedule({
        contractId: String(contractId),
        currency,
      }),
    enabled: !!contractId,
  });

  const isNocStatusEditable = useCallback(
    (nocStatus: OriginalPremiumScheduleResponseDtoNocStatusEnum) => {
      const editableStatuses: OriginalPremiumScheduleResponseDtoNocStatusEnum[] =
        [
          OriginalPremiumScheduleResponseDtoNocStatusEnum.NocDue,
          OriginalPremiumScheduleResponseDtoNocStatusEnum.NocIssued,
          OriginalPremiumScheduleResponseDtoNocStatusEnum.ExtensionGranted,
        ];
      return editableStatuses.includes(nocStatus);
    },
    []
  );

  const getNocStatusOptions = useCallback(
    (data: PremiumScheduleValue): SelectionOption[] => {
      // Base options that users can select
      const selectableOptions: SelectionOption[] = [
        {
          value: OriginalPremiumScheduleResponseDtoNocStatusEnum.NocDue,
          label: OriginalPremiumScheduleResponseDtoNocStatusEnum.NocDue,
        },
        {
          value: OriginalPremiumScheduleResponseDtoNocStatusEnum.NocIssued,
          label: OriginalPremiumScheduleResponseDtoNocStatusEnum.NocIssued,
        },
        {
          value:
            OriginalPremiumScheduleResponseDtoNocStatusEnum.ExtensionGranted,
          label:
            OriginalPremiumScheduleResponseDtoNocStatusEnum.ExtensionGranted,
        },
      ];

      // If current value is not in the selectable options, add it for display
      const currentValue = data.nocStatus;
      if (
        currentValue &&
        !selectableOptions.some((opt) => opt.value === currentValue)
      ) {
        selectableOptions.push({
          value: currentValue,
          label: currentValue,
        });
      }

      return selectableOptions;
    },
    []
  );

  const updateNocStatusMutation = useMutation(
    async ({
      premiumScheduleId,
      nocStatus,
      closing,
      closingReference,
    }: {
      premiumScheduleId: string;
      nocStatus?: OriginalPremiumScheduleResponseDtoNocStatusEnum;
      closing: boolean;
      closingReference?: string;
    }) => {
      return premiumScheduleApi.updatePremiumSchedule({
        premiumScheduleId,
        updatePremiumSchedulePayloadDto: {
          nocStatus,
          closing,
          closingReference,
        },
      });
    },
    {
      onSuccess: () => {
        refetch();
      },
      onError: async (error: { response: Response }) => {
        handleError({
          error,
          defaultMessage: t('premiumProcessing.updateNocStatusFail'),
        });
      },
    }
  );

  const onDataChange = useCallback(
    async (affectedRow: RowData<PremiumScheduleValue>) => {
      await updateNocStatusMutation.mutateAsync({
        premiumScheduleId: affectedRow.data.premiumScheduleId,
        nocStatus: affectedRow.data.nocStatus,
        closing: affectedRow.data.closing ?? false,
        closingReference: affectedRow.data.closingReference,
      });
    },
    [updateNocStatusMutation]
  );

  const columnDefs = useMemo<ExcelTableColDef[]>(
    () => [
      {
        headerName: t('premiumProcessing.documentNo'),
        field: 'documentNo',
        flex: 1,
        minWidth: 200,
        editable: false,
        resizable: true,
      },
      {
        headerName: t('premiumProcessing.description'),
        field: 'description',
        flex: 1,
        minWidth: 200,
        editable: false,
        resizable: true,
      },
      {
        headerName: t('premiumProcessing.insured'),
        field: 'insured',
        flex: 1,
        minWidth: 200,
        editable: false,
        resizable: true,
      },
      {
        headerName: t('premiumProcessing.ppwDueDate'),
        field: 'ppwDueDate',
        flex: 1,
        minWidth: 200,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.premiumStatus'),
        field: 'premiumStatus',
        flex: 1,
        minWidth: 200,
        resizable: true,
        editable: false,
        cellRenderer: (params: ICellRendererParams) => {
          if (!params.value) return null;
          return (
            <PremiumStatusIndicator
              status={params.value as PremiumDueResponseDtoPremiumStatusEnum}
            />
          );
        },
      },
      {
        headerName: t('premiumDue.closing'),
        field: 'closing',
        flex: 1,
        minWidth: 100,
        resizable: true,
        editable: true,
        columnType: EExcelTableColumnType.CHECKBOX,
      },
      {
        headerName: t('premiumProcessing.closingReference'),
        field: 'closingReference',
        flex: 1,
        minWidth: 200,
        editable: true,
        resizable: true,
      },
      {
        headerName: t('premiumDue.noticeOfCancellationStatus'),
        field: 'nocStatus',
        flex: 1,
        minWidth: 200,
        resizable: true,
        editable: (params) => isNocStatusEditable(params.data?.nocStatus),
        columnType: EExcelTableColumnType.SELECTION,
        selectionOptions: getNocStatusOptions,
      },
      {
        headerName: t('premiumProcessing.shareOfPremiumSigned'),
        field: 'shareOfPremiumSigned',
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        flex: 1,
        minWidth: 200,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.gstOnPremium'),
        field: 'gstOnPremium',
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        minWidth: 200,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.cedingCommission'),
        field: 'cedingCommission',
        type: ECellValueType.NUMERIC_COLUMN,
        flex: 1,
        minWidth: 200,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.gstOnCedingCommission'),
        field: 'gstOnCedingCommission',
        type: ECellValueType.NUMERIC_COLUMN,
        flex: 1,
        minWidth: 200,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.sourceBrokerage'),
        field: 'sourceBrokerage',
        type: ECellValueType.NUMERIC_COLUMN,
        flex: 1,
        minWidth: 200,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.gstOnSourceBrokerage'),
        field: 'gstOnSourceBrokerage',
        type: ECellValueType.NUMERIC_COLUMN,
        flex: 1,
        minWidth: 200,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.ere'),
        field: 'ere',
        minWidth: 200,
        type: ECellValueType.NUMERIC_COLUMN,
        resizable: true,
        editable: false,
        flex: 1,
      },
      {
        headerName: t('premiumProcessing.withholdingTax'),
        field: 'withholdingTax',
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        minWidth: 200,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.gstOnEre'),
        field: 'gstOnEre',
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        minWidth: 200,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.otherDeductions'),
        field: 'otherDeductions',
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        minWidth: 200,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.netPremiumDueFromBroker'),
        field: 'netPremiumDueFromBroker',
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        minWidth: 200,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.pinnacleCommission'),
        field: 'pinnacleCommission',
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        minWidth: 200,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.ait'),
        field: 'ait',
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        minWidth: 200,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.taxesAndFees'),
        field: 'taxesAndFees',
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        minWidth: 200,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('premiumProcessing.binderCashRemittance'),
        field: 'binderCashRemittance',
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        minWidth: 200,
        resizable: true,
        editable: false,
      },
    ],
    [t, isNocStatusEditable]
  );

  const data = useMemo(() => {
    return originalPremiumSchedule?.map((item) => ({
      ...item,
      ppwDueDate: dayjs(item.ppwDueDate).format('DD/MM/YYYY'),
      closing: item.closing ?? false,
    }));
  }, [originalPremiumSchedule]);

  return (
    <>
      <FormSection
        title={t('premiumProcessing.originalPremiumSchedule')}
        collapsible
      >
        <ExcelTable
          data={data}
          columnDefs={columnDefs}
          includeIdRow={false}
          tableContainerClassName="h-[24rem]"
          loading={isLoadingOriginalPremiumSchedule}
          onDataChange={onDataChange}
        />
      </FormSection>
    </>
  );
};

export default OriginalPremiumSchedule;
